import { useEffect } from 'react';
import { ArrowUpRight, Instagram, Youtube, Mail, ShoppingBag } from 'lucide-react';
import { DarkChapter } from '@/components/layout/DarkChapter';
import { AudioWaveform } from '@/components/AudioWaveform';
import { SimpleReveal } from '@/components/SimpleReveal';
import { trackLead } from '@/lib/metaPixel';
import damoPhoto from '@/assets/damian-keyes-founder.png';

const utm = (campaign: string) =>
  `utm_medium=insta+bio&utm_source=insta+bio&utm_campaign=${encodeURIComponent(campaign)}`;

type LinkItem = {
  emoji: string;
  title: string;
  url: string;
  lead?: boolean;
};

const groups: { label: string; links: LinkItem[] }[] = [
  {
    label: 'How To Work Together',
    links: [
      {
        emoji: '🎧',
        title: 'Send Your Music',
        url: 'mailto:hello@dk-mba.com?subject=My%20Music',
      },
      {
        emoji: '🤝',
        title: 'Apply To Work With Me Here',
        url: `https://a23j5fry30t.typeform.com/to/CzjaFT5z?${utm('🤝  apply to work with me here')}`,
        lead: true,
      },
      {
        emoji: '📈',
        title: 'Learn How To Release Music',
        url: `https://www.dk-mba.com?${utm('📈 learn how to release music')}`,
      },
      {
        emoji: '🚀',
        title: 'Work With Me & My Team (Application Only)',
        url: `https://www.dk-artists.com?${utm('🚀  work with me & my team (application only)')}`,
      },
    ],
  },
  {
    label: 'The Free Stuff',
    links: [
      {
        emoji: '🆓',
        title: 'Get Your Free 25 Day Single Release Plan',
        url: `https://training.dk-mba.com/25dayplan?${utm('🆓  get your free 25 day single release plan (works for first single onwards)')}`,
      },
      {
        emoji: '🪝',
        title: 'Get 100 Free Hooks For Your Social Media',
        url: `https://training.dk-mba.com/100-HOOKS/?${utm('🆓  get 100 free hooks for your social media')}`,
      },
      {
        emoji: '⚡',
        title: 'Subscribe on YouTube (University Level Education For Free)',
        url: `https://www.youtube.com/damiankeyes?${utm('⚡ subscribe on youtube (university level education for free)')}`,
      },
    ],
  },
];

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 1 1-1.85-2.48V9.75a5.68 5.68 0 1 0 4.94 5.63V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3a4.29 4.29 0 0 1-3.24-1.48Z" />
  </svg>
);

const socials = [
  { label: 'Instagram', href: 'https://instagram.com/damiankeyes1', Icon: Instagram },
  { label: 'TikTok', href: 'https://tiktok.com/@damiankeyes', Icon: TikTokIcon },
  { label: 'YouTube', href: 'https://www.youtube.com/damiankeyes', Icon: Youtube },
  { label: 'Amazon', href: 'https://www.amazon.com/Damian-Keyes/e/B07DRJ1FYV', Icon: ShoppingBag },
  { label: 'Email', href: 'mailto:hello@dk-mba.com', Icon: Mail },
];

export default function LinkTree() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Damian Keyes | Links, Free Resources & Music Marketing';

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta(
      'description',
      'All of Damian Keyes\u2019 links in one place: send your music, apply to work with his team, free release plans, 100 free hooks and YouTube.'
    );

    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const prevCanonical = canonical?.getAttribute('href') ?? null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://dk-artists.com/dk');

    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: 'Damian Keyes',
      description: "Let's get your music heard.",
      url: 'https://dk-artists.com/dk',
      sameAs: socials.filter((s) => !s.href.startsWith('mailto')).map((s) => s.href),
    });
    document.head.appendChild(ld);

    return () => {
      document.title = prevTitle;
      if (prevCanonical) canonical?.setAttribute('href', prevCanonical);
      ld.remove();
    };
  }, []);

  let index = 0;

  return (
    <DarkChapter>
      <main className="min-h-screen px-5 py-12 lg:py-16">
        <div className="w-full max-w-md mx-auto flex flex-col items-center text-center">
          <SimpleReveal>
            <AudioWaveform />
          </SimpleReveal>

          <SimpleReveal delay={60}>
            <div className="relative mt-4 mb-5">
              <div
                className="absolute -inset-6 rounded-full blur-2xl pointer-events-none"
                style={{
                  background:
                    'radial-gradient(circle at center, rgba(0,113,227,0.45) 0%, transparent 70%)',
                }}
              />
              <img
                src={damoPhoto}
                alt="Damian Keyes, music marketing coach and founder of DK Artists"
                width={128}
                height={128}
                className="relative h-28 w-28 rounded-full object-cover object-top border border-white/15 shadow-[0_8px_30px_rgba(0,0,0,0.6)]"
              />
            </div>
          </SimpleReveal>

          <SimpleReveal delay={120}>
            <h1 className="font-headline font-bold text-white text-3xl tracking-[-0.02em]">
              Damian Keyes
            </h1>
            <p className="text-white/60 text-sm mt-2">Let&rsquo;s get your music heard.</p>
          </SimpleReveal>

          <SimpleReveal delay={180}>
            <nav aria-label="Social links" className="flex items-center gap-2 mt-5 mb-8">
              {socials.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target={href.startsWith('mailto') ? undefined : '_blank'}
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="h-10 w-10 flex items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/70 transition-all duration-300 hover:text-white hover:border-[#0071e3]/50 hover:bg-white/10 hover:-translate-y-0.5"
                >
                  <Icon className="h-[18px] w-[18px]" />
                </a>
              ))}
            </nav>
          </SimpleReveal>

          <div className="w-full space-y-8">
            {groups.map((group) => (
              <section key={group.label} className="w-full">
                <SimpleReveal delay={220 + index * 10}>
                  <h2 className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/40 mb-3">
                    &gt; {group.label}
                  </h2>
                </SimpleReveal>

                <div className="space-y-3">
                  {group.links.map((link) => {
                    index += 1;
                    const isMail = link.url.startsWith('mailto');
                    return (
                      <SimpleReveal key={link.title} delay={240 + index * 60}>
                        <a
                          href={link.url}
                          target={isMail ? undefined : '_blank'}
                          rel="noopener noreferrer"
                          onClick={link.lead ? trackLead : undefined}
                          className="group flex items-center gap-3 w-full rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-sm px-4 py-4 text-left transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/[0.07] hover:border-[#0071e3]/50 hover:shadow-[0_0_24px_rgba(0,113,227,0.25)] active:translate-y-0 active:scale-[0.99]"
                        >
                          <span aria-hidden="true" className="text-lg leading-none shrink-0">
                            {link.emoji}
                          </span>
                          <span className="flex-1 text-sm font-medium text-white leading-snug">
                            {link.title}
                          </span>
                          <ArrowUpRight className="h-4 w-4 shrink-0 text-white/35 transition-all duration-300 group-hover:text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </a>
                      </SimpleReveal>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>

          <SimpleReveal delay={900}>
            <p className="text-white/25 text-xs mt-12">
              &copy; {new Date().getFullYear()} Damian Keyes
            </p>
          </SimpleReveal>
        </div>
      </main>
    </DarkChapter>
  );
}
